import { useState, useEffect } from 'react';
import { useOutletContext, Navigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import ImageUpload from '../../components/ImageUpload';
import { Globe, Phone, MapPin, Facebook, Instagram, Twitter, Save, MessageSquare } from 'lucide-react';

interface LayoutContext {
  viewAsStoreId?: string;
  viewAsStoreName?: string;
}

interface StoreProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  website?: string;
  googleHoursUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  twitterUrl?: string;
  logoUrl?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  successMessage?: string | null;
  status: string;
}

export default function StoreSettingsPage() {
  const { viewAsStoreId } = useOutletContext<LayoutContext>() || {};
  const [store, setStore] = useState<StoreProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editable fields
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [website, setWebsite] = useState('');
  const [googleHoursUrl, setGoogleHoursUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#000000');
  const [secondaryColor, setSecondaryColor] = useState('#FFFFFF');
  const [successMessage, setSuccessMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Redirect if not viewing as a store
  if (!viewAsStoreId) {
    return <Navigate to="/admin/stores" replace />;
  }

  useEffect(() => {
    loadStore();
  }, [viewAsStoreId]);

  useEffect(() => {
    if (store) {
      const changes =
        name !== store.name ||
        phone !== (store.phone || '') ||
        address !== (store.address || '') ||
        city !== (store.city || '') ||
        state !== (store.state || '') ||
        zipCode !== (store.zipCode || '') ||
        website !== (store.website || '') ||
        googleHoursUrl !== (store.googleHoursUrl || '') ||
        facebookUrl !== (store.facebookUrl || '') ||
        instagramUrl !== (store.instagramUrl || '') ||
        twitterUrl !== (store.twitterUrl || '') ||
        primaryColor !== (store.primaryColor || '#000000') ||
        secondaryColor !== (store.secondaryColor || '#FFFFFF') ||
        successMessage !== (store.successMessage || '');
      setHasChanges(changes);
    }
  }, [name, phone, address, city, state, zipCode, website, googleHoursUrl, facebookUrl, instagramUrl, twitterUrl, primaryColor, secondaryColor, successMessage, store]);

  const loadStore = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: StoreProfile }>(
        `/admin/stores/${viewAsStoreId}`
      );
      const storeData = response.data.data;
      setStore(storeData);
      setName(storeData.name || '');
      setPhone(storeData.phone || '');
      setAddress(storeData.address || '');
      setCity(storeData.city || '');
      setState(storeData.state || '');
      setZipCode(storeData.zipCode || '');
      setWebsite(storeData.website || '');
      setGoogleHoursUrl(storeData.googleHoursUrl || '');
      setFacebookUrl(storeData.facebookUrl || '');
      setInstagramUrl(storeData.instagramUrl || '');
      setTwitterUrl(storeData.twitterUrl || '');
      setPrimaryColor(storeData.primaryColor || '#000000');
      setSecondaryColor(storeData.secondaryColor || '#FFFFFF');
      setSuccessMessage(storeData.successMessage || '');
    } catch (error) {
      console.error('Failed to load store:', error);
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!store) return;

    setSaving(true);
    try {
      const response = await apiClient.put<{ success: boolean; data: StoreProfile }>(
        `/admin/stores/${store.id}`,
        {
          phone: phone || null,
          address: address || null,
          city: city || null,
          state: state || null,
          zipCode: zipCode || null,
          website: website || null,
          googleHoursUrl: googleHoursUrl || null,
          facebookUrl: facebookUrl || null,
          instagramUrl: instagramUrl || null,
          twitterUrl: twitterUrl || null,
          primaryColor,
          secondaryColor,
          successMessage: successMessage || null,
        }
      );
      setStore(response.data.data);
      setMessage({ type: 'success', text: 'Profile saved successfully' });
      setHasChanges(false);
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to save profile';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (base64Data: string) => {
    if (!store) return;

    const response = await apiClient.post<{ success: boolean; data: { logoUrl: string } }>(
      `/admin/stores/${store.id}/logo`,
      { image: base64Data }
    );

    setStore({ ...store, logoUrl: response.data.data.logoUrl });
    setMessage({ type: 'success', text: 'Logo uploaded successfully' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleLogoDelete = async () => {
    if (!store) return;

    await apiClient.delete(`/admin/stores/${store.id}/logo`);
    setStore({ ...store, logoUrl: null });
    setMessage({ type: 'success', text: 'Logo removed' });
    setTimeout(() => setMessage(null), 3000);
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (!store) {
    return <div className="text-red-600">Failed to load profile</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Store Profile</h1>
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo Section */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Store Logo</h2>
          <ImageUpload
            currentImageUrl={store.logoUrl}
            onUpload={handleLogoUpload}
            onDelete={handleLogoDelete}
            label=""
            aspectRatio="square"
            recommendedDimensions="400x400px (square)"
            helpText="Your logo will be automatically resized to max 400x400px and converted to WebP format for fast mobile loading. Square images work best."
          />
        </div>

        {/* Basic Info Section */}
        <div className="card lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Store Name</label>
              <p className="text-gray-900 py-2 font-medium">{store.name}</p>
              <p className="text-xs text-gray-400">Store name cannot be changed</p>
            </div>
            <div>
              <label className="label">Email</label>
              <p className="text-gray-900 py-2">{store.email}</p>
              <p className="text-xs text-gray-400">Contact admin to change email</p>
            </div>
            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 text-gray-400 mt-3" />
              <div className="flex-1">
                <label className="label">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Globe className="w-4 h-4 text-gray-400 mt-3" />
              <div className="flex-1">
                <label className="label">Website</label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="input"
                  placeholder="https://yourstore.com"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-start gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gray-400 mt-1" />
              <label className="text-sm font-medium text-gray-500">Address</label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="input"
                  placeholder="Street Address"
                />
              </div>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="input"
                placeholder="City"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="input"
                  placeholder="State"
                />
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  className="input"
                  placeholder="ZIP"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div>
              <label className="label">Google Hours URL</label>
              <input
                type="url"
                value={googleHoursUrl}
                onChange={(e) => setGoogleHoursUrl(e.target.value)}
                className="input"
                placeholder="https://g.page/yourstore"
              />
            </div>
          </div>
        </div>

        {/* Social Links Section */}
        <div className="card lg:col-span-3">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Social Media</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-2">
              <Facebook className="w-4 h-4 text-gray-400 mt-3" />
              <div className="flex-1">
                <label className="label">Facebook</label>
                <input
                  type="url"
                  value={facebookUrl}
                  onChange={(e) => setFacebookUrl(e.target.value)}
                  className="input"
                  placeholder="https://facebook.com/yourstore"
                />
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Instagram className="w-4 h-4 text-gray-400 mt-3" />
              <div className="flex-1">
                <label className="label">Instagram</label>
                <input
                  type="url"
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  className="input"
                  placeholder="https://instagram.com/yourstore"
                />
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Twitter className="w-4 h-4 text-gray-400 mt-3" />
              <div className="flex-1">
                <label className="label">Twitter/X</label>
                <input
                  type="url"
                  value={twitterUrl}
                  onChange={(e) => setTwitterUrl(e.target.value)}
                  className="input"
                  placeholder="https://twitter.com/yourstore"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Branding Section */}
        <div className="card lg:col-span-3">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Brand Colors</h2>
          <p className="text-sm text-gray-500 mb-4">
            These colors are used on your QR code printouts and customer-facing pages.
          </p>
          <div className="flex flex-wrap gap-8">
            <div>
              <label className="label">Primary Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-12 rounded-lg border border-gray-200 cursor-pointer p-1"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="input w-28 font-mono text-sm"
                  placeholder="#000000"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Used for text and QR code</p>
            </div>
            <div>
              <label className="label">Secondary Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-12 h-12 rounded-lg border border-gray-200 cursor-pointer p-1"
                />
                <input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="input w-28 font-mono text-sm"
                  placeholder="#FFFFFF"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Used for backgrounds</p>
            </div>
          </div>

          {/* Color Preview */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <label className="text-sm font-medium text-gray-500 mb-3 block">Preview</label>
            <div
              className="p-4 rounded-lg flex items-center justify-between"
              style={{ backgroundColor: secondaryColor }}
            >
              <span style={{ color: primaryColor }} className="font-semibold">
                {name || store.name}
              </span>
              {store.logoUrl && (
                <img src={store.logoUrl} alt="" className="w-8 h-8 object-contain" />
              )}
            </div>
          </div>
        </div>

        {/* Success Message Section */}
        <div className="card lg:col-span-3">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Customer Success Message</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            This message is displayed to customers after they successfully scan your QR code and enter the raffle.
            Leave blank to use the default message.
          </p>
          <div>
            <label className="label">Custom Success Message</label>
            <textarea
              value={successMessage}
              onChange={(e) => setSuccessMessage(e.target.value)}
              className="input min-h-[100px]"
              placeholder="You have been entered into the raffle and will be contacted by email if you won a prize."
              rows={4}
            />
            <p className="text-xs text-gray-400 mt-2">
              Default: "You have been entered into the raffle and will be contacted by email if you won a prize."
            </p>
          </div>
        </div>
      </div>

      {/* Floating save button for mobile */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 lg:hidden">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2 shadow-lg"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}
